// ============================================================================
// RESULTS PREVIEW PAGE - High-Converting Paywall v2
// ALL TEXT USES TRANSLATION KEYS (react-i18next)
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Lock, CheckCircle, AlertTriangle, Shield, TrendingUp, Clock, Zap, Crown, 
  ChevronRight, ChevronDown, Star, Users, FileText, Target, ArrowRight,
  ShieldCheck, RefreshCw, BarChart3, Sparkles, X
} from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { supabase } from '../lib/supabase';
import Header from '../components/Header';

// Stripe Payment Links — update these with your actual Stripe links
const STRIPE_MONTHLY_LINK = 'https://buy.stripe.com/3cI28s6TA9pZdDxaze4Rq04';
const STRIPE_ANNUAL_LINK = 'https://buy.stripe.com/4gM7sM0vc0TtdDx7n24Rq05';

// ============================================================================
// CSS KEYFRAMES (injected once)
// ============================================================================
const injectStyles = () => {
  if (document.getElementById('results-preview-styles')) return;
  const style = document.createElement('style');
  style.id = 'results-preview-styles';
  style.textContent = `
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes pulseGlow {
      0%, 100% { box-shadow: 0 0 20px rgba(20, 184, 166, 0.3); }
      50% { box-shadow: 0 0 40px rgba(20, 184, 166, 0.6); }
    }
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    @keyframes progressFill {
      from { width: 0%; }
      to { width: var(--target-width); }
    }
    @keyframes countPulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
    @keyframes slideInFromBottom {
      from { transform: translateY(100%); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    .animate-fade-in-up {
      animation: fadeInUp 0.6s ease-out forwards;
      opacity: 0;
    }
    .animate-fade-in {
      animation: fadeIn 0.4s ease-out forwards;
      opacity: 0;
    }
    .animate-pulse-glow {
      animation: pulseGlow 2s ease-in-out infinite;
    }
    .animate-shimmer {
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
      background-size: 200% 100%;
      animation: shimmer 2s infinite;
    }
    .animate-slide-in-bottom {
      animation: slideInFromBottom 0.4s ease-out forwards;
    }
    .blur-content {
      filter: blur(6px);
      user-select: none;
      pointer-events: none;
    }
    .blur-overlay {
      position: relative;
    }
    .blur-overlay::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(to bottom, transparent 0%, rgba(249,250,251,0.8) 40%, rgba(249,250,251,1) 100%);
      pointer-events: none;
    }
    .sticky-cta-bar {
      animation: slideInFromBottom 0.4s ease-out forwards;
    }
  `;
  document.head.appendChild(style);
};

// ============================================================================
// ANALYZING INTERSTITIAL
// ============================================================================
const AnalyzingInterstitial = ({ onComplete, t }) => {
  const [step, setStep] = useState(0);
  const [progress, setProgress] = useState(0);

  const steps = [
    { text: t('resultsPreview.analyzing.step1'), icon: BarChart3 },
    { text: t('resultsPreview.analyzing.step2'), icon: Users },
    { text: t('resultsPreview.analyzing.step3'), icon: TrendingUp },
    { text: t('resultsPreview.analyzing.step4'), icon: Sparkles },
  ];

  useEffect(() => {
    injectStyles();
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) { clearInterval(progressInterval); return 100; }
        return prev + 1.5;
      });
    }, 50);

    const stepTimers = steps.map((_, i) =>
      setTimeout(() => setStep(i), i * 1200)
    );

    const completeTimer = setTimeout(onComplete, 5000);
    return () => {
      clearInterval(progressInterval);
      stepTimers.forEach(clearTimeout);
      clearTimeout(completeTimer);
    };
  }, []);

  const CurrentIcon = steps[step]?.icon || BarChart3;

  return (
    <div className="min-h-screen bg-[#0a0b14] flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <Link to="/" className="font-serif text-2xl font-bold mb-12 inline-block">
          <span className="text-white">Lab</span>
          <span className="text-teal-400">2040</span>
        </Link>

        <div className="w-20 h-20 bg-teal-500/20 rounded-2xl flex items-center justify-center mx-auto mb-8 animate-pulse-glow">
          <CurrentIcon className="w-10 h-10 text-teal-400" />
        </div>

        <div className="space-y-3 mb-10">
          {steps.map((s, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 transition-all duration-500 ${
                i <= step ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
              }`}
            >
              {i < step ? (
                <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0" />
              ) : i === step ? (
                <div className="w-5 h-5 border-2 border-teal-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
              ) : (
                <div className="w-5 h-5 rounded-full border border-gray-600 flex-shrink-0" />
              )}
              <span className={`text-sm ${i <= step ? 'text-white' : 'text-gray-600'}`}>
                {s.text}
              </span>
            </div>
          ))}
        </div>

        <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-teal-500 to-teal-300 rounded-full transition-all duration-100 ease-linear"
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
        <p className="text-gray-500 text-xs mt-3">
          {Math.min(Math.round(progress), 100)}% {t('resultsPreview.analyzing.complete')}
        </p>
      </div>
    </div>
  );
};

// ============================================================================
// SPEEDOMETER COMPONENT
// ============================================================================
const RiskSpeedometer = ({ score }) => {
  const [animatedScore, setAnimatedScore] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setAnimatedScore(score), 100);
    return () => clearTimeout(timer);
  }, [score]);

  const angleInDegrees = 180 - (animatedScore / 100) * 180;
  const angleInRadians = (angleInDegrees * Math.PI) / 180;
  const needleLength = 65;
  const centerX = 90, centerY = 90;
  const needleX = centerX + needleLength * Math.cos(angleInRadians);
  const needleY = centerY - needleLength * Math.sin(angleInRadians);

  return (
    <div className="relative">
      <svg width="180" height="100" viewBox="0 0 180 100">
        <defs>
          <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#22c55e"/>
            <stop offset="25%" stopColor="#84cc16"/>
            <stop offset="50%" stopColor="#eab308"/>
            <stop offset="75%" stopColor="#f97316"/>
            <stop offset="100%" stopColor="#ef4444"/>
          </linearGradient>
        </defs>
        <path d="M 15 90 A 75 75 0 0 1 165 90" stroke="rgba(255,255,255,0.2)" strokeWidth="12" fill="none" strokeLinecap="round"/>
        <path d="M 15 90 A 75 75 0 0 1 165 90" stroke="url(#gaugeGradient)" strokeWidth="12" fill="none" strokeLinecap="round"/>
        <line x1={centerX} y1={centerY} x2={needleX} y2={needleY} stroke="white" strokeWidth="3" strokeLinecap="round"
          style={{ transition: 'all 1.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}/>
        <circle cx={centerX} cy={centerY} r="10" fill="white" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}/>
        <circle cx={centerX} cy={centerY} r="5" fill="rgba(0,0,0,0.2)"/>
      </svg>
    </div>
  );
};

// ============================================================================
// RADAR CHART COMPONENT
// ============================================================================
const RadarChart = ({ data, t }) => {
  const { industryScore = 50, riskScore = 50, knowledgeScore = 50, readinessScore = 50 } = data;

  return (
    <div className="flex-shrink-0">
      <svg width="200" height="220" viewBox="0 0 200 220">
        <g stroke="rgba(255,255,255,0.2)" fill="none" strokeWidth="1">
          <polygon points="100,20 180,110 100,200 20,110"/>
          <polygon points="100,42.5 157.5,110 100,177.5 42.5,110"/>
          <polygon points="100,65 135,110 100,155 65,110"/>
          <polygon points="100,87.5 112.5,110 100,132.5 87.5,110"/>
          <line x1="100" y1="20" x2="100" y2="200"/>
          <line x1="20" y1="110" x2="180" y2="110"/>
        </g>
        <polygon
          points={`100,${110 - (readinessScore / 100) * 80} ${100 + (industryScore / 100) * 80},110 100,${110 + (knowledgeScore / 100) * 80} ${100 - (riskScore / 100) * 80},110`}
          fill="rgba(255,255,255,0.2)" stroke="rgba(255,255,255,0.8)" strokeWidth="2"/>
        <circle cx={100} cy={110 - (readinessScore / 100) * 80} r="5" fill="white"/>
        <circle cx={100 + (industryScore / 100) * 80} cy={110} r="5" fill="white"/>
        <circle cx={100} cy={110 + (knowledgeScore / 100) * 80} r="5" fill="white"/>
        <circle cx={100 - (riskScore / 100) * 80} cy={110} r="5" fill="white"/>
        <text x="100" y="12" textAnchor="middle" fill="white" fontSize="12" fontWeight="500">{t('scores.readinessScore')}</text>
        <text x="192" y="110" textAnchor="middle" fill="white" fontSize="12" fontWeight="500" transform="rotate(90, 192, 110)">{t('scores.industryRisk')}</text>
        <text x="100" y="218" textAnchor="middle" fill="white" fontSize="12" fontWeight="500">{t('scores.aiKnowledge')}</text>
        <text x="8" y="110" textAnchor="middle" fill="white" fontSize="12" fontWeight="500" transform="rotate(-90, 8, 110)">{t('scores.taskExposure')}</text>
      </svg>
    </div>
  );
};

// ============================================================================
// COUNTDOWN TIMER
// ============================================================================
const CountdownTimer = ({ t }) => {
  const [timeLeft, setTimeLeft] = useState(() => {
    const saved = localStorage.getItem('lab2040_results_timer');
    if (saved) {
      const remaining = parseInt(saved) - Date.now();
      return remaining > 0 ? Math.floor(remaining / 1000) : 0;
    }
    const expiry = Date.now() + 24 * 60 * 60 * 1000;
    localStorage.setItem('lab2040_results_timer', expiry.toString());
    return 24 * 60 * 60;
  });

  useEffect(() => {
    if (timeLeft <= 0) return;
    const interval = setInterval(() => setTimeLeft(prev => Math.max(0, prev - 1)), 1000);
    return () => clearInterval(interval);
  }, [timeLeft]);

  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor((timeLeft % 3600) / 60);
  const seconds = timeLeft % 60;
  const pad = (n) => n.toString().padStart(2, '0');

  return (
    <div className="flex items-center gap-2 text-sm">
      <Clock className="w-4 h-4 text-amber-500" />
      <span className="text-gray-600">{t('resultsPreview.urgencyBar.reportAvailable')}</span>
      <span className="font-mono font-bold text-gray-900 bg-amber-50 px-2 py-0.5 rounded">
        {pad(hours)}:{pad(minutes)}:{pad(seconds)}
      </span>
    </div>
  );
};

// ============================================================================
// FAQ ACCORDION
// ============================================================================
const FAQItem = ({ question, answer, isOpen, onClick, isRTL }) => (
  <div className="border-b border-gray-100 last:border-0">
    <button onClick={onClick} className="w-full flex items-center justify-between py-4 text-left hover:text-teal-600 transition-colors">
      <span className={`font-medium text-gray-900 ${isRTL ? 'pl-4' : 'pr-4'}`}>{question}</span>
      <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-40 pb-4' : 'max-h-0'}`}>
      <p className="text-gray-600 text-sm leading-relaxed">{answer}</p>
    </div>
  </div>
);

// ============================================================================
// HELPER: Map risk levels to translation keys
// ============================================================================
const getRiskLevelKey = (level) => ({
  CRITICAL: 'riskLevels.critical',
  HIGH: 'riskLevels.high',
  MODERATE: 'riskLevels.moderate',
  LOW: 'riskLevels.low',
  MINIMAL: 'riskLevels.minimal'
}[level] || 'riskLevels.moderate');

const getTimelineKey = (horizon) => ({
  IMMEDIATE: 'resultsPreview.hero.timeline.immediate',
  SHORT_TERM: 'resultsPreview.hero.timeline.shortTerm',
  MEDIUM_TERM: 'resultsPreview.hero.timeline.mediumTerm',
  LONG_TERM: 'resultsPreview.hero.timeline.longTerm'
}[horizon] || 'resultsPreview.hero.timeline.varies');

const getUrgencyKey = (level) => ({
  CRITICAL: 'resultsPreview.hero.urgency.critical',
  HIGH: 'resultsPreview.hero.urgency.high',
  MODERATE: 'resultsPreview.hero.urgency.moderate',
  LOW: 'resultsPreview.hero.urgency.low',
  MINIMAL: 'resultsPreview.hero.urgency.minimal'
}[level] || 'resultsPreview.hero.urgency.moderate');

const getRiskClass = (level) => ({
  CRITICAL: 'bg-red-100 text-red-700',
  HIGH: 'bg-orange-100 text-orange-700',
  MODERATE: 'bg-yellow-100 text-yellow-700',
  LOW: 'bg-green-100 text-green-700',
  MINIMAL: 'bg-teal-100 text-teal-700'
}[level] || 'bg-yellow-100 text-yellow-700');

const getRiskGradient = (level) => ({
  CRITICAL: 'from-red-600 via-red-500 to-red-400',
  HIGH: 'from-red-600 via-red-500 to-red-400',
  MODERATE: 'from-amber-500 via-amber-600 to-yellow-400',
  LOW: 'from-teal-600 via-teal-500 to-teal-400',
  MINIMAL: 'from-teal-600 via-teal-500 to-teal-400'
}[level] || 'from-amber-500 via-amber-600 to-yellow-400');

const getRiskColorClass = (level) => ({
  CRITICAL: 'text-red-600',
  HIGH: 'text-red-600',
  MODERATE: 'text-amber-600',
  LOW: 'text-teal-600',
  MINIMAL: 'text-teal-600'
}[level] || 'text-amber-600');

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const ResultsPreview = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const { user, hasSubscription, isAuthenticated } = useAuth();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAnalyzing, setShowAnalyzing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('annual');
  const [openFAQ, setOpenFAQ] = useState(null);
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const pricingRef = useRef(null);

  const isPro = hasSubscription ? hasSubscription() : false;

  // Save assessment to database for logged-in users
  const saveAssessment = async (resultsData) => {
    if (!user) return;
    try {
      const storedAnswers = localStorage.getItem('lab2040_assessment_answers');
      const validUserTypes = ['employee', 'business', 'student'];
      let userType = resultsData.userType || 'employee';
      if (!validUserTypes.includes(userType)) userType = 'employee';

      const { error } = await supabase.from('assessments').insert({
        user_id: user.id,
        answers: storedAnswers ? JSON.parse(storedAnswers) : {},
        composite_score: resultsData.compositeScore,
        risk_level: resultsData.riskLevel,
        user_type: userType,
        industry_score: resultsData.industryScore || null,
        profession_score: resultsData.professionScore || null,
        knowledge_score: resultsData.knowledgeScore || null,
        readiness_score: resultsData.readinessScore || null,
        risk_score: resultsData.riskScore || null,
        timeline: resultsData.timeHorizon || null,
        key_risk_factors: resultsData.keyRiskFactors || [],
        protective_factors: resultsData.protectiveFactors || [],
        recommendations: resultsData.recommendations || [],
        completed_at: new Date().toISOString()
      });
      if (error) console.error('Database error:', error);
      else console.log('Assessment saved successfully');
    } catch (err) {
      console.error('Error saving assessment:', err);
    }
  };

  useEffect(() => {
    injectStyles();
    const storedResults = localStorage.getItem('lab2040_assessment_results');
    const alreadySaved = localStorage.getItem('lab2040_assessment_saved');
    const alreadyAnalyzed = sessionStorage.getItem('lab2040_analyzed');

    if (storedResults) {
      const parsedResults = JSON.parse(storedResults);
      setResults(parsedResults);

      if (!alreadyAnalyzed && !isPro) {
        setShowAnalyzing(true);
        sessionStorage.setItem('lab2040_analyzed', 'true');
      }

      if (!alreadySaved && user) {
        saveAssessment(parsedResults);
        localStorage.setItem('lab2040_assessment_saved', 'true');
      }
    } else {
      navigate('/assessment');
    }
    setLoading(false);
  }, [navigate, user]);

  // Sticky CTA visibility
  useEffect(() => {
    if (isPro) return;
    const handleScroll = () => {
      if (!pricingRef.current) return;
      const rect = pricingRef.current.getBoundingClientRect();
      setShowStickyCTA(rect.bottom < 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isPro]);

  const handleUnlockReport = () => {
    window.location.href = selectedPlan === 'annual' ? STRIPE_ANNUAL_LINK : STRIPE_MONTHLY_LINK;
  };

  const scrollToPricing = () => {
    pricingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // Extract user context from stored answers for personalization
  const getPersonalContext = () => {
    try {
      const answers = JSON.parse(localStorage.getItem('lab2040_assessment_answers') || '{}');
      return {
        industry: answers.industry || answers.sector || t('resultsPreview.personalized.defaultIndustry', 'your industry'),
        role: answers.jobTitle || answers.role || answers.profession || t('resultsPreview.personalized.defaultRole', 'your role'),
      };
    } catch { return { industry: t('resultsPreview.personalized.defaultIndustry', 'your industry'), role: t('resultsPreview.personalized.defaultRole', 'your role') }; }
  };

  const faqItems = [
    { q: t('resultsPreview.faq.q1'), a: t('resultsPreview.faq.a1') },
    { q: t('resultsPreview.faq.q2'), a: t('resultsPreview.faq.a2') },
    { q: t('resultsPreview.faq.q3'), a: t('resultsPreview.faq.a3') },
    { q: t('resultsPreview.faq.q4'), a: t('resultsPreview.faq.a4') },
    { q: t('resultsPreview.faq.q5'), a: t('resultsPreview.faq.a5') },
  ];

  // ---- Loading state ----
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-teal-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!results) return null;

  // ---- Analyzing Interstitial ----
  if (showAnalyzing) {
    return <AnalyzingInterstitial onComplete={() => setShowAnalyzing(false)} t={t} />;
  }

  const context = getPersonalContext();
  const riskFactors = results.keyRiskFactors || [];
  const protectiveFactors = results.protectiveFactors || [];
  const recommendations = results.recommendations || [];
  const timelineText = t(getTimelineKey(results.timeHorizon));
  const riskLevelText = t(getRiskLevelKey(results.riskLevel));

  return (
    <div className="min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* ================================================================
          HEADER (with language switcher built in)
      ================================================================ */}
      <Header variant="dark" />

      <main className="max-w-4xl mx-auto px-4 py-8">

        {/* ================================================================
            1. HERO — Score + Speedometer
        ================================================================ */}
        <div className={`bg-gradient-to-br ${getRiskGradient(results.riskLevel)} rounded-3xl p-6 md:p-8 text-white mb-6 animate-fade-in-up`}>
          <p className="text-white/70 uppercase tracking-widest text-sm mb-6">
            {t('resultsPreview.hero.title')}
          </p>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div className="flex items-center gap-6">
              <RiskSpeedometer score={results.compositeScore} />
              <div>
                <div className="flex items-baseline">
                  <span className="text-6xl md:text-7xl font-bold" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.2)' }}>
                    {results.compositeScore}
                  </span>
                  <span className="text-2xl text-white/50 ml-1">/100</span>
                </div>
                <div className={`inline-block px-4 py-2 rounded-full text-sm font-bold uppercase ${getRiskClass(results.riskLevel)} mt-2`}>
                  {riskLevelText} {t('riskLevels.risk')}
                </div>
              </div>
            </div>
            <RadarChart data={results} t={t} />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-6 text-sm">
            <div className="flex items-center gap-2 text-white/80">
              <Clock className="w-4 h-4" />
              <span>{t('resultsPreview.hero.expectedImpact', { timeline: timelineText })}</span>
            </div>
            <span className="hidden sm:inline text-white/30">•</span>
            <div className="flex items-center gap-2 text-white/80">
              <AlertTriangle className="w-4 h-4" />
              <span>{t(getUrgencyKey(results.riskLevel))}</span>
            </div>
          </div>
        </div>

        {/* ================================================================
            PRO USERS — Full Access Section
        ================================================================ */}
        {isPro && (
          <>
            <div className="bg-gradient-to-r from-teal-600 to-gray-700 rounded-2xl shadow-lg overflow-hidden mb-8">
              <div className="p-6 md:p-8 text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Crown className="w-8 h-8 text-white" />
                </div>
                <h2 className="font-serif text-2xl font-bold text-white mb-2">{t('resultsPreview.proMember.title')}</h2>
                <p className="text-white/80 mb-6">{t('resultsPreview.proMember.subtitle')}</p>
                <Link to="/results/full"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white text-teal-600 font-bold rounded-xl hover:bg-gray-100 transition-colors">
                  {t('resultsPreview.proMember.viewReport')} <ChevronRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { icon: '🏢', value: results.industryScore, label: t('scores.industryRisk') },
                { icon: '📋', value: results.riskScore, label: t('scores.taskExposure') },
                { icon: '🧠', value: results.knowledgeScore, label: t('scores.aiKnowledge') },
                { icon: '🎯', value: results.readinessScore, label: t('scores.readinessScore') }
              ].map((stat, i) => (
                <div key={i} className="bg-white rounded-xl p-4 text-center shadow-sm">
                  <div className="text-2xl mb-1">{stat.icon}</div>
                  <div className="font-serif text-2xl font-bold text-gray-900">{stat.value || 0}%</div>
                  <div className="text-xs text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>

            {riskFactors.length > 0 && (
              <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="font-serif text-xl font-bold text-gray-900">{t('scores.keyRiskFactors')}</h3>
                </div>
                <ul className="space-y-3">
                  {riskFactors.map((factor, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-700">
                      <span className="text-red-500 mt-1">•</span>{factor}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        {/* ================================================================
            NON-PRO: CONVERSION FUNNEL
        ================================================================ */}
        {!isPro && (
          <>
            {/* ---- 2. PERSONALIZED SUMMARY ---- */}
            <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <p className="text-gray-700 leading-relaxed">
                {t('resultsPreview.personalized.summary', {
                  role: context.role,
                  industry: context.industry,
                  riskLevel: riskLevelText.toLowerCase(),
                  timeline: timelineText.toLowerCase(),
                  interpolation: { escapeValue: false }
                }).split(/<strong>|<\/strong>|<riskLevel>|<\/riskLevel>/)
                  .map((part, i) => {
                    // Bold segments (role, industry, timeline) are at odd indices from <strong> splits
                    // riskLevel segment needs special coloring
                    if (part === context.role || part === context.industry || part === timelineText.toLowerCase()) {
                      return <strong key={i} className="text-gray-900">{part}</strong>;
                    }
                    if (part.includes(riskLevelText.toLowerCase())) {
                      return <span key={i} className={`font-bold ${getRiskColorClass(results.riskLevel)}`}>{part}</span>;
                    }
                    return <span key={i}>{part}</span>;
                  })
                }
              </p>
            </div>

            {/* ---- 3. FREE RISK FACTORS (2 shown, rest locked) ---- */}
            {riskFactors.length > 0 && (
              <div className="bg-white rounded-2xl p-6 mb-2 shadow-sm animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg font-bold text-gray-900">{t('resultsPreview.riskFactors.title')}</h3>
                    <p className="text-xs text-gray-500">
                      {riskFactors.length > 2
                        ? t('resultsPreview.riskFactors.showing', { shown: 2, total: riskFactors.length })
                        : t('resultsPreview.riskFactors.basedOn')
                      }
                    </p>
                  </div>
                </div>

                <ul className="space-y-3 mb-4">
                  {riskFactors.slice(0, 2).map((factor, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-700 py-2">
                      <span className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-red-600 text-xs font-bold">{i + 1}</span>
                      </span>
                      <span>{factor}</span>
                    </li>
                  ))}
                </ul>

                {riskFactors.length > 2 && (
                  <div className="blur-overlay relative">
                    <ul className="space-y-3 blur-content">
                      {riskFactors.slice(2, 5).map((factor, i) => (
                        <li key={i} className="flex items-start gap-3 text-gray-700 py-2">
                          <span className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-red-600 text-xs font-bold">{i + 3}</span>
                          </span>
                          <span>{factor}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                      <button onClick={scrollToPricing}
                        className="flex items-center gap-2 bg-white/95 backdrop-blur-sm px-5 py-3 rounded-xl shadow-lg border border-gray-200 hover:border-teal-300 hover:shadow-xl transition-all">
                        <Lock className="w-4 h-4 text-teal-600" />
                        <span className="font-semibold text-gray-900 text-sm">
                          {t('resultsPreview.riskFactors.unlockMore', { count: riskFactors.length - 2 })}
                        </span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ---- 4. BLURRED ACTION PLAN TEASER ---- */}
            {recommendations.length > 0 && (
              <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                    <Target className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg font-bold text-gray-900">{t('resultsPreview.actionPlan.title')}</h3>
                    <p className="text-xs text-gray-500">{t('resultsPreview.actionPlan.subtitle')}</p>
                  </div>
                </div>
                <div className="blur-overlay relative">
                  <ul className="space-y-3 blur-content">
                    {recommendations.slice(0, 4).map((rec, i) => (
                      <li key={i} className="flex items-start gap-3 text-gray-700 py-2">
                        <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="absolute inset-0 flex items-center justify-center z-10">
                    <button onClick={scrollToPricing}
                      className="flex items-center gap-2 bg-white/95 backdrop-blur-sm px-5 py-3 rounded-xl shadow-lg border border-gray-200 hover:border-teal-300 hover:shadow-xl transition-all">
                      <Lock className="w-4 h-4 text-teal-600" />
                      <span className="font-semibold text-gray-900 text-sm">{t('resultsPreview.actionPlan.unlockPlan')}</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ---- 5. SOCIAL PROOF ---- */}
            <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="flex -space-x-2">
                    {['bg-teal-500', 'bg-blue-500', 'bg-purple-500', 'bg-orange-500'].map((color, i) => (
                      <div key={i} className={`w-8 h-8 ${color} rounded-full border-2 border-white flex items-center justify-center`}>
                        <span className="text-white text-xs font-bold">{['S', 'M', 'A', 'R'][i]}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">
                      {t('resultsPreview.socialProof.professionalsAssessed', { count: '2,400' })}
                    </p>
                    <div className="flex items-center gap-1">
                      {[1,2,3,4,5].map(i => <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />)}
                      <span className="text-xs text-gray-500 ml-1">{t('resultsPreview.socialProof.averageRating')}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-400">
                  <span className="font-medium">{t('resultsPreview.socialProof.poweredBy')}</span>
                  <span className="font-bold text-gray-500">WEF</span>
                  <span className="text-gray-300">|</span>
                  <span className="font-bold text-gray-500">McKinsey</span>
                  <span className="text-gray-300">|</span>
                  <span className="font-bold text-gray-500">Goldman Sachs</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-gray-600 text-sm italic leading-relaxed">
                  "{t('resultsPreview.socialProof.testimonial1.text')}"
                </p>
                <p className="text-xs text-gray-400 mt-2 font-medium">— {t('resultsPreview.socialProof.testimonial1.author')}</p>
              </div>
            </div>

            {/* ---- 6. URGENCY BAR ---- */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex flex-col sm:flex-row items-center justify-between gap-3 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
              <CountdownTimer t={t} />
              <button onClick={scrollToPricing} className="text-teal-600 font-semibold text-sm hover:text-teal-700 flex items-center gap-1">
                {t('resultsPreview.urgencyBar.unlockNow')} <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {/* ---- 7. PRICING SECTION ---- */}
            <div ref={pricingRef} className="bg-gradient-to-br from-[#0a0b14] to-[#1a1b2e] rounded-3xl p-6 md:p-8 text-white mb-6 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
              
              <div className="text-center mb-8">
                <h2 className="font-serif text-2xl md:text-3xl font-bold mb-3">
                  {t('resultsPreview.pricing.title')}
                </h2>
                <p className="text-gray-400 max-w-lg mx-auto text-sm">
                  {t('resultsPreview.pricing.subtitle')}
                </p>
              </div>

              {/* Plan toggle */}
              <div className="flex justify-center mb-6">
                <div className="bg-white/10 rounded-xl p-1 flex">
                  <button
                    onClick={() => setSelectedPlan('monthly')}
                    className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedPlan === 'monthly' ? 'bg-white text-gray-900 shadow' : 'text-gray-400 hover:text-white'
                    }`}>
                    {t('subscription.monthly')}
                  </button>
                  <button
                    onClick={() => setSelectedPlan('annual')}
                    className={`px-5 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                      selectedPlan === 'annual' ? 'bg-white text-gray-900 shadow' : 'text-gray-400 hover:text-white'
                    }`}>
                    {t('subscription.annual')}
                    <span className="bg-teal-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                      {t('subscription.save50')}
                    </span>
                  </button>
                </div>
              </div>

              {/* Price display */}
              <div className="text-center mb-8">
                {selectedPlan === 'annual' ? (
                  <>
                    <div className="flex items-center justify-center gap-3 mb-1">
                      <span className="text-gray-500 line-through text-lg">$4.99{t('subscription.perMonth')}</span>
                      <span className="bg-teal-500/20 text-teal-400 text-xs px-2 py-1 rounded-full font-bold">{t('resultsPreview.pricing.fiftyOff')}</span>
                    </div>
                    <div className="flex items-baseline justify-center">
                      <span className="text-5xl font-bold">$2.50</span>
                      <span className="text-gray-400 ml-1">{t('subscription.perMonth')}</span>
                    </div>
                    <p className="text-gray-500 text-sm mt-1">{t('subscription.billedAnnually', { amount: '$29.99' })}</p>
                  </>
                ) : (
                  <>
                    <div className="flex items-baseline justify-center">
                      <span className="text-5xl font-bold">$4.99</span>
                      <span className="text-gray-400 ml-1">{t('subscription.perMonth')}</span>
                    </div>
                    <p className="text-gray-500 text-sm mt-1">{t('subscription.billedMonthly')} • {t('subscription.switchToAnnual')}</p>
                  </>
                )}
              </div>

              {/* Outcome-based features */}
              <div className="bg-white/5 rounded-2xl p-5 mb-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: BarChart3, text: t('resultsPreview.pricing.features.f1') },
                    { icon: Target, text: t('resultsPreview.pricing.features.f2') },
                    { icon: TrendingUp, text: t('resultsPreview.pricing.features.f3') },
                    { icon: RefreshCw, text: t('resultsPreview.pricing.features.f4') },
                    { icon: FileText, text: t('resultsPreview.pricing.features.f5') },
                    { icon: Sparkles, text: t('resultsPreview.pricing.features.f6') }
                  ].map(({ icon: Icon, text }, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-teal-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon className="w-4 h-4 text-teal-400" />
                      </div>
                      <span className="text-sm text-gray-300 leading-snug">{text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Button */}
              <button
                onClick={handleUnlockReport}
                className="w-full py-4 bg-teal-500 hover:bg-teal-400 text-white font-bold text-lg rounded-xl transition-all shadow-lg hover:shadow-xl hover:shadow-teal-500/25 animate-pulse-glow">
                {t('resultsPreview.pricing.cta')}
              </button>

              {/* Reassurance row */}
              <div className="flex flex-wrap items-center justify-center gap-4 mt-5 text-xs text-gray-500">
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-teal-500" />
                  <span>{t('subscription.securePayment')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <X className="w-3 h-3" />
                  <span>{t('subscription.cancelAnytime')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <RefreshCw className="w-4 h-4 text-teal-500" />
                  <span>{t('subscription.moneyBackGuarantee')}</span>
                </div>
              </div>
            </div>

            {/* ---- 8. SECOND TESTIMONIAL ---- */}
            <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">AK</span>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-900 text-sm">{t('resultsPreview.socialProof.testimonial2.author')}</span>
                    <div className="flex items-center gap-0.5">
                      {[1,2,3,4,5].map(i => <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />)}
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm italic leading-relaxed">
                    "{t('resultsPreview.socialProof.testimonial2.text')}"
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {t('resultsPreview.socialProof.testimonial2.role')} • {t('resultsPreview.socialProof.testimonial2.timeAgo')}
                  </p>
                </div>
              </div>
            </div>

            {/* ---- 9. FAQ ---- */}
            <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm">
              <h3 className="font-serif text-lg font-bold text-gray-900 mb-2">{t('resultsPreview.faq.title')}</h3>
              {faqItems.map((item, i) => (
                <FAQItem
                  key={i}
                  question={item.q}
                  answer={item.a}
                  isOpen={openFAQ === i}
                  onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                  isRTL={isRTL}
                />
              ))}
            </div>

            {/* ---- 10. FINAL CTA ---- */}
            <div className="text-center mb-6 bg-gray-100 rounded-2xl p-6">
              <p className="text-gray-600 mb-4 text-sm">
                {t('resultsPreview.finalCta.text')}
              </p>
              <button
                onClick={scrollToPricing}
                className="px-8 py-3 bg-teal-600 hover:bg-teal-500 text-white font-bold rounded-xl transition-all shadow-lg text-sm">
                {t('resultsPreview.finalCta.button')}
              </button>
            </div>
          </>
        )}

        {/* ================================================================
            FOOTER LINKS
        ================================================================ */}
        {!isAuthenticated && (
          <div className="text-center p-4 bg-gray-100 rounded-xl mb-4">
            <p className="text-gray-600 mb-2 text-sm">{t('resultsPreview.signIn')}</p>
            <Link to="/login" className="text-teal-600 hover:text-teal-700 font-semibold text-sm">{t('resultsPreview.signInLink')}</Link>
          </div>
        )}

        <div className="text-center mt-4 mb-8">
          <Link to={isPro ? "/dashboard" : "/assessment"} className="text-gray-500 hover:text-teal-600 font-medium text-sm">
            ← {isPro ? t('cta.backToDashboard') : t('resultsPreview.retakeAssessment')}
          </Link>
        </div>
      </main>

      {/* ================================================================
          STICKY MOBILE CTA
      ================================================================ */}
      {!isPro && showStickyCTA && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl p-3 z-50 sticky-cta-bar">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
            <div className="hidden sm:block">
              <p className="text-sm font-semibold text-gray-900">
                {selectedPlan === 'annual' ? '$2.50' : '$4.99'}{t('subscription.perMonth')}
              </p>
              <p className="text-xs text-gray-500">{t('subscription.cancelAnytime')}</p>
            </div>
            <button
              onClick={handleUnlockReport}
              className="flex-1 sm:flex-none px-6 py-3 bg-teal-600 hover:bg-teal-500 text-white font-bold rounded-xl transition-all shadow-lg text-sm">
              {t('resultsPreview.pricing.cta')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultsPreview;
